/**
 * Prepare an image for CDN upload.
 *
 * Production nginx in front of api.cixon.finance historically uses the default
 * `client_max_body_size 1m`. Requests over ~1 MB never reach Express — nginx
 * returns a HTML 413 without CORS headers, which the browser reports as a CORS
 * failure. App-side we advertise 5 MB (Express UPLOAD_MAX_FILE_BYTES), so a
 * 1.1 MB photo looks valid but still fails at the proxy.
 *
 * Compress anything above the proxy-safe budget down to JPEG under that budget.
 */

/** Stay safely under a typical nginx 1m body limit (multipart overhead included). */
export const UPLOAD_PROXY_SAFE_MAX_BYTES = 900 * 1024;

const MAX_EDGE_PX = 2400;

function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image for upload compression'));
    };
    img.src = url;
  });
}

function canvasToJpegBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Image compression failed'));
          return;
        }
        resolve(blob);
      },
      'image/jpeg',
      quality,
    );
  });
}

function drawScaled(img: HTMLImageElement, maxEdge: number): HTMLCanvasElement {
  const scale = Math.min(1, maxEdge / Math.max(img.naturalWidth, img.naturalHeight));
  const width = Math.max(1, Math.round(img.naturalWidth * scale));
  const height = Math.max(1, Math.round(img.naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas unavailable for image compression');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);
  return canvas;
}

/**
 * Return the original file when already under the proxy-safe budget and browser-
 * friendly; otherwise re-encode as JPEG under UPLOAD_PROXY_SAFE_MAX_BYTES.
 */
export async function prepareImageForUpload(file: File): Promise<File> {
  if (!file.type.startsWith('image/')) return file;
  // SVG / GIF: keep as-is (canvas would flatten animation / lose vectors badly).
  if (file.type === 'image/svg+xml' || file.type === 'image/gif') return file;
  if (file.size <= UPLOAD_PROXY_SAFE_MAX_BYTES && !/heic|heif|tiff/i.test(file.type)) {
    return file;
  }

  const img = await loadImageFromFile(file);
  let maxEdge = MAX_EDGE_PX;
  let quality = 0.85;
  let blob: Blob | null = null;

  for (let attempt = 0; attempt < 8; attempt += 1) {
    const canvas = drawScaled(img, maxEdge);
    blob = await canvasToJpegBlob(canvas, quality);
    if (blob.size <= UPLOAD_PROXY_SAFE_MAX_BYTES) break;
    if (quality > 0.55) {
      quality -= 0.1;
    } else {
      maxEdge = Math.round(maxEdge * 0.75);
      quality = 0.8;
    }
  }

  if (!blob) return file;

  const baseName = file.name.replace(/\.[^.]+$/, '') || 'image';
  return new File([blob], `${baseName}.jpg`, {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
}
